import React, { useState, createContext, useContext } from "react";

// Confirmation Context
const ConfirmationContext = createContext();

// Confirmation Provider
export const ConfirmationProvider = ({ children }) => {
  const [confirmationState, setConfirmationState] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "Confirm",
    cancelText: "Cancel",
    onConfirm: null,
    onCancel: null,
    type: "danger", // danger, warning, info
  });

  const showConfirmation = ({
    title = "Confirm Action",
    message = "Are you sure you want to proceed?",
    confirmText = "Confirm",
    cancelText = "Cancel",
    type = "danger",
    onConfirm = () => {},
    onCancel = () => {},
  }) => {
    return new Promise((resolve) => {
      setConfirmationState({
        isOpen: true,
        title,
        message,
        confirmText,
        cancelText,
        type,
        onConfirm: () => {
          onConfirm();
          resolve(true);
          closeConfirmation();
        },
        onCancel: () => {
          onCancel();
          resolve(false);
          closeConfirmation();
        },
      });
    });
  };

  const closeConfirmation = () => {
    setConfirmationState((prev) => ({ ...prev, isOpen: false }));
  };

  return (
    <ConfirmationContext.Provider
      value={{ showConfirmation, closeConfirmation }}
    >
      {children}
      {confirmationState.isOpen && (
        <ConfirmationModal {...confirmationState} onClose={closeConfirmation} />
      )}
    </ConfirmationContext.Provider>
  );
};

// Hook to use confirmation
export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error("useConfirmation must be used within ConfirmationProvider");
  }
  return context;
};

// Confirmation Modal Component
const ConfirmationModal = ({
  title,
  message,
  confirmText,
  cancelText,
  type,
  onConfirm,
  onCancel,
  onClose,
}) => {
  const getTypeStyles = (type) => {
    switch (type) {
      case "danger":
        return {
          iconColor: "#ef4444",
          confirmBg: "#ef4444",
          confirmHover: "#dc2626",
          icon: "🗑️",
        };
      case "warning":
        return {
          iconColor: "#f59e0b",
          confirmBg: "#f59e0b",
          confirmHover: "#d97706",
          icon: "⚠️",
        };
      case "info":
        return {
          iconColor: "#3b82f6",
          confirmBg: "#3b82f6",
          confirmHover: "#2563eb",
          icon: "ℹ️",
        };
      default:
        return {
          iconColor: "#6b7280",
          confirmBg: "#374151",
          confirmHover: "#1f2937",
          icon: "❓",
        };
    }
  };

  const typeStyles = getTypeStyles(type);

  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000,
    animation: "fadeIn 0.2s ease-out",
  };

  const modalStyle = {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "24px",
    maxWidth: "400px",
    width: "90%",
    boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.25)",
    animation: "scaleIn 0.2s ease-out",
    fontFamily:
      'Inter, Roboto, "SF Pro Display", "Segoe UI", system-ui, -apple-system, sans-serif',
  };

  const headerStyle = {
    display: "flex",
    alignItems: "center",
    marginBottom: "16px",
  };

  const iconStyle = {
    fontSize: "24px",
    marginRight: "12px",
    color: typeStyles.iconColor,
  };

  const titleStyle = {
    fontSize: "18px",
    fontWeight: "600",
    color: "#1f2937",
    margin: 0,
  };

  const messageStyle = {
    fontSize: "14px",
    color: "#6b7280",
    lineHeight: "1.5",
    marginBottom: "24px",
  };

  const buttonContainerStyle = {
    display: "flex",
    gap: "12px",
    justifyContent: "flex-end",
  };

  const buttonBaseStyle = {
    padding: "10px 20px",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "500",
    border: "none",
    cursor: "pointer",
    transition: "all 0.2s ease",
    fontFamily: "inherit",
  };

  const cancelButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "1px solid #d1d5db",
  };

  const confirmButtonStyle = {
    ...buttonBaseStyle,
    backgroundColor: typeStyles.confirmBg,
    color: "white",
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
          
          @keyframes scaleIn {
            from { 
              opacity: 0;
              transform: scale(0.9) translateY(-10px);
            }
            to { 
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
        `}
      </style>
      <div style={overlayStyle} onClick={onClose}>
        <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
          <div style={headerStyle}>
            <span style={iconStyle}>{typeStyles.icon}</span>
            <h3 style={titleStyle}>{title}</h3>
          </div>

          <p style={messageStyle}>{message}</p>

          <div style={buttonContainerStyle}>
            <button
              style={cancelButtonStyle}
              onClick={onCancel}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#e5e7eb";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#f3f4f6";
              }}
            >
              {cancelText}
            </button>
            <button
              style={confirmButtonStyle}
              onClick={onConfirm}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = typeStyles.confirmHover;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = typeStyles.confirmBg;
              }}
            >
              {confirmText}
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

// Demo Component
const ConfirmationDemo = () => {
  const { showConfirmation } = useConfirmation();

  const handleDeleteCustomer = async () => {
    const confirmed = await showConfirmation({
      title: "Delete Customer",
      message:
        "Are you sure you want to delete this customer? This action cannot be undone.",
      confirmText: "Delete",
      cancelText: "Cancel",
      type: "danger",
    });

    if (confirmed) {
      alert("Customer deleted!");
    }
  };

  const handleSaveChanges = async () => {
    const confirmed = await showConfirmation({
      title: "Save Changes",
      message: "Do you want to save the changes made to this customer?",
      confirmText: "Save",
      cancelText: "Discard",
      type: "info",
    });

    if (confirmed) {
      alert("Changes saved!");
    }
  };

  const handleWarningAction = async () => {
    const confirmed = await showConfirmation({
      title: "Warning",
      message:
        "This action will modify existing data. Are you sure you want to continue?",
      confirmText: "Continue",
      cancelText: "Cancel",
      type: "warning",
    });

    if (confirmed) {
      alert("Action completed!");
    }
  };

  const buttonStyle = {
    padding: "12px 24px",
    border: "none",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    transition: "all 0.2s ease",
    marginRight: "12px",
    marginBottom: "12px",
  };

  return (
    <div
      style={{ padding: "40px", fontFamily: "Inter, system-ui, sans-serif" }}
    >
      <h1 style={{ marginBottom: "32px", color: "#1f2937" }}>
        Professional Confirmation Modals
      </h1>

      <p style={{ marginBottom: "32px", color: "#6b7280", lineHeight: "1.6" }}>
        Replace browser's default confirm() with these professional modals that
        match your app's design.
      </p>

      <div style={{ marginBottom: "32px" }}>
        <button
          style={{
            ...buttonStyle,
            backgroundColor: "#ef4444",
            color: "white",
          }}
          onClick={handleDeleteCustomer}
          onMouseEnter={(e) => (e.target.style.backgroundColor = "#dc2626")}
          onMouseLeave={(e) => (e.target.style.backgroundColor = "#ef4444")}
        >
          Delete Customer (Danger)
        </button>

        <button
          style={{
            ...buttonStyle,
            backgroundColor: "#3b82f6",
            color: "white",
          }}
          onClick={handleSaveChanges}
          onMouseEnter={(e) => (e.target.style.backgroundColor = "#2563eb")}
          onMouseLeave={(e) => (e.target.style.backgroundColor = "#3b82f6")}
        >
          Save Changes (Info)
        </button>

        <button
          style={{
            ...buttonStyle,
            backgroundColor: "#f59e0b",
            color: "white",
          }}
          onClick={handleWarningAction}
          onMouseEnter={(e) => (e.target.style.backgroundColor = "#d97706")}
          onMouseLeave={(e) => (e.target.style.backgroundColor = "#f59e0b")}
        >
          Warning Action (Warning)
        </button>
      </div>

      <div
        style={{
          backgroundColor: "#f8fafc",
          padding: "24px",
          borderRadius: "8px",
          border: "1px solid #e2e8f0",
        }}
      >
        <h3 style={{ color: "#1e293b", marginBottom: "16px" }}>
          How to integrate in AllCustomer.jsx:
        </h3>

        <pre
          style={{
            backgroundColor: "#1e293b",
            color: "#e2e8f0",
            padding: "16px",
            borderRadius: "8px",
            fontSize: "13px",
            lineHeight: "1.6",
            overflow: "auto",
          }}
        ></pre>
      </div>
    </div>
  );
};
